import React, { useState, useRef, useCallback } from "react";
import { toPng } from "html-to-image";
import toast from "react-hot-toast";
import { getToken } from "../utils/auth";

const API = process.env.REACT_APP_API_URL || "/api";

let nextId = 1;

export default function AutoMukhi() {
  const [image, setImage]         = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [mediaType, setMediaType] = useState("image/jpeg");
  const [mukhis, setMukhis]       = useState([]);
  const [center, setCenter]       = useState({ x: 50, y: 50 });
  const [sizeText, setSizeText]   = useState("19-21");
  const [showSize, setShowSize]   = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [draggingCenter, setDraggingCenter] = useState(false);

  const containerRef = useRef(null);
  const dragRef      = useRef(null);
  const rafRef       = useRef(null);
  const didDragRef   = useRef(false);

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMediaType(file.type || "image/jpeg");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setImage(dataUrl);
      // Strip the data URL prefix to get raw base64
      setImageBase64(dataUrl.split(",")[1]);
      setMukhis([]);
      nextId = 1;
      setSelectedId(null);
      setCenter({ x: 50, y: 50 });
    };
    reader.readAsDataURL(file);
  };

  const autoDetect = async () => {
    if (!imageBase64) return toast.error("Upload an image first");
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/analyze-mukhi`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ imageBase64, mediaType }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);

      const { positions, center: c, mukhiCount } = data.data;
      nextId = 1;
      setMukhis(positions.map((p) => ({ id: nextId++, x: p.x, y: p.y })));
      if (c) setCenter({ x: c.x, y: c.y });
      toast.success(`${mukhiCount} Mukhi detected!`);
    } catch (err) {
      toast.error(err.message || "Auto detect failed");
      console.error(err);
    } finally {
      setAnalyzing(false);
    }
  };

  // ── Drag (mukhi dots) ──────────────────────────────────────────────────────
  const startDrag = useCallback((clientX, clientY, id) => {
    didDragRef.current = false;
    setSelectedId(id);
    const rect = containerRef.current.getBoundingClientRect();
    setMukhis((prev) => {
      const m = prev.find((m) => m.id === id);
      dragRef.current = {
        type: "mukhi", id,
        offsetX: clientX - (m.x / 100) * rect.width,
        offsetY: clientY - (m.y / 100) * rect.height,
      };
      return prev;
    });
  }, []);

  const startCenterDrag = useCallback((clientX, clientY) => {
    didDragRef.current = false;
    setDraggingCenter(true);
    const rect = containerRef.current.getBoundingClientRect();
    dragRef.current = {
      type: "center",
      offsetX: clientX - (center.x / 100) * rect.width,
      offsetY: clientY - (center.y / 100) * rect.height,
    };
  }, [center]);

  const moveDrag = useCallback((clientX, clientY) => {
    if (!dragRef.current) return;
    didDragRef.current = true;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!dragRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((clientX - dragRef.current.offsetX) / rect.width)  * 100));
      const y = Math.min(100, Math.max(0, ((clientY - dragRef.current.offsetY) / rect.height) * 100));
      if (dragRef.current.type === "mukhi") {
        setMukhis((p) => p.map((m) => (m.id === dragRef.current?.id ? { ...m, x, y } : m)));
      } else {
        setCenter({ x, y });
      }
    });
  }, []);

  const stopDrag = useCallback(() => {
    dragRef.current = null;
    setDraggingCenter(false);
    setTimeout(() => { didDragRef.current = false; }, 0);
  }, []);

  const handleCanvasClick = (e) => {
    if (didDragRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width)  * 100;
    const y = ((e.clientY - rect.top)  / rect.height) * 100;
    const m = { id: nextId++, x, y };
    setMukhis((p) => [...p, m]);
    setSelectedId(m.id);
  };

  const removeMukhi = (id) => {
    setMukhis((p) => p.filter((m) => m.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const resetAll = () => { setMukhis([]); nextId = 1; setSelectedId(null); };

  const download = async () => {
    if (!image) return toast.error("Please upload an image first");
    setCapturing(true);
    setSelectedId(null);
    await new Promise((r) => setTimeout(r, 150));
    try {
      const dataUrl = await toPng(containerRef.current, { pixelRatio: 3, cacheBust: true });
      const a = document.createElement("a");
      a.download = `makalu-auto-mukhi-${Date.now()}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("Downloaded!");
    } catch (err) {
      toast.error("Download failed");
      console.error(err);
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#0f0f0f" }}>
      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "#FFD700", fontFamily: "serif" }}>Auto Mukhi</h1>
            <p className="text-sm mt-1" style={{ color: "#8A6000" }}>Upload → AI automatically detects &amp; numbers mukhi → Download</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <label className="btn-secondary text-sm px-4 py-2 cursor-pointer">
              Upload Photo
              <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
            </label>
            {image && <>
              <button onClick={autoDetect} disabled={analyzing}
                className="text-sm px-4 py-2 rounded-lg font-semibold"
                style={{
                  background: analyzing ? "rgba(200,150,12,0.2)" : "linear-gradient(135deg,#8A6000,#C8960C,#FFD700,#C8960C,#8A6000)",
                  color: analyzing ? "#8A6000" : "#0a0a0a",
                  border: "none", cursor: analyzing ? "not-allowed" : "pointer",
                  fontWeight: 800, letterSpacing: "0.05em",
                }}>
                {analyzing ? "⏳ Detecting…" : "✦ Auto Detect"}
              </button>
              {mukhis.length > 0 && (
                <button onClick={resetAll}
                  className="text-sm px-4 py-2 rounded-lg font-semibold"
                  style={{ background: "rgba(127,29,29,0.2)", color: "#fca5a5", border: "1px solid rgba(127,29,29,0.4)", cursor: "pointer" }}>
                  Reset
                </button>
              )}
              <button onClick={download} disabled={capturing}
                className="text-sm px-4 py-2 rounded-lg font-semibold"
                style={{ background: capturing ? "#1a3a1a" : "linear-gradient(135deg,#1a6e1a,#28a428)", color: "#fff", border: "none", cursor: capturing ? "not-allowed" : "pointer" }}>
                {capturing ? "Saving…" : "↓ Download PNG"}
              </button>
            </>}
          </div>
        </div>

        <div className="flex gap-5" style={{ alignItems: "flex-start" }}>

          {/* Canvas */}
          <div className="flex-1 rounded-2xl overflow-hidden"
            style={{ background: "#1a1a1a", border: "1px solid rgba(200,150,12,0.2)", minHeight: 480 }}>

            {!image ? (
              <label className="flex flex-col items-center justify-center cursor-pointer" style={{ minHeight: 480 }}>
                <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(200,150,12,0.07)", border: "1px solid rgba(200,150,12,0.2)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                  <span style={{ fontSize: 32 }}>✦</span>
                </div>
                <p style={{ color: "#5a4a30", fontSize: 16, fontWeight: 600 }}>Upload Rudraksha photo</p>
                <p style={{ color: "#3a2a00", fontSize: 12, marginTop: 4 }}>AI will auto-detect mukhi</p>
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              </label>
            ) : (
              <div
                ref={containerRef}
                style={{ position: "relative", display: "block", lineHeight: 0, userSelect: "none" }}
                onMouseMove={(e) => moveDrag(e.clientX, e.clientY)}
                onMouseUp={stopDrag}
                onMouseLeave={stopDrag}
                onTouchMove={(e) => { e.preventDefault(); moveDrag(e.touches[0].clientX, e.touches[0].clientY); }}
                onTouchEnd={stopDrag}
                onClick={handleCanvasClick}
              >
                <img src={image} alt="Rudraksha" draggable={false}
                  style={{ width: "100%", display: "block", maxHeight: "75vh", objectFit: "contain" }} />

                {/* Dashed lines SVG */}
                <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
                  {mukhis.map((m) => (
                    <line key={m.id}
                      x1={`${center.x}%`} y1={`${center.y}%`}
                      x2={`${m.x}%`}      y2={`${m.y}%`}
                      stroke="#6b3a1f" strokeWidth="1.8" strokeDasharray="5,4"
                    />
                  ))}
                </svg>

                {/* Center dot (hidden during capture) */}
                {!capturing && (
                  <div
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); startCenterDrag(e.clientX, e.clientY); }}
                    onTouchStart={(e) => { e.stopPropagation(); startCenterDrag(e.touches[0].clientX, e.touches[0].clientY); }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute", left: `${center.x}%`, top: `${center.y}%`,
                      transform: "translate(-50%,-50%)", width: 14, height: 14, borderRadius: "50%",
                      background: draggingCenter ? "#FFD700" : "rgba(255,215,0,0.35)",
                      border: "2px dashed #FFD700", cursor: "move", touchAction: "none", zIndex: 10,
                    }}
                  />
                )}

                {/* Mukhi numbered dots */}
                {mukhis.map((m, idx) => {
                  const sel = !capturing && m.id === selectedId;
                  return (
                    <div key={m.id}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); startDrag(e.clientX, e.clientY, m.id); }}
                      onTouchStart={(e) => { e.stopPropagation(); startDrag(e.touches[0].clientX, e.touches[0].clientY, m.id); }}
                      onClick={(e) => { e.stopPropagation(); setSelectedId(m.id); }}
                      style={{
                        position: "absolute", left: `${m.x}%`, top: `${m.y}%`,
                        transform: "translate(-50%,-50%)", width: 22, height: 22, borderRadius: "50%",
                        background: "#5a2d0c", border: sel ? "2px solid #FFD700" : "2px solid #8B4513",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "grab", touchAction: "none", zIndex: 5,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.5)",
                      }}>
                      <span style={{ color: "#fff", fontSize: 11, fontWeight: 800, fontFamily: "Arial, sans-serif", lineHeight: 1 }}>
                        {idx + 1}
                      </span>
                    </div>
                  );
                })}

                {/* Size indicator */}
                {showSize && (
                  <div style={{ position: "absolute", bottom: "3%", left: "50%", transform: "translateX(-50%)", textAlign: "center", pointerEvents: "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                      <span style={{ color: "#5a2d0c", fontSize: 14, fontWeight: 700 }}>←·····</span>
                      <span style={{ color: "#3a1a00", fontSize: 13, fontWeight: 800, fontFamily: "Arial, sans-serif" }}>{sizeText}</span>
                      <span style={{ color: "#5a2d0c", fontSize: 14, fontWeight: 700 }}>·····→</span>
                    </div>
                    <div style={{ color: "#5a2d0c", fontSize: 10, fontWeight: 600, fontFamily: "Arial, sans-serif", marginTop: 1 }}>(in mm)</div>
                  </div>
                )}

                {/* Analyzing overlay */}
                {analyzing && (
                  <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 20 }}>
                    <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin mb-4"
                      style={{ borderColor: "#C8960C", borderTopColor: "transparent" }} />
                    <p style={{ color: "#FFD700", fontSize: 14, fontWeight: 700, fontFamily: "serif" }}>AI analyzing mukhi…</p>
                    <p style={{ color: "#8A6000", fontSize: 11, marginTop: 4 }}>This may take 10-15 seconds</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Controls */}
          {image && (
            <div style={{ width: 272, flexShrink: 0 }}>

              <div className="rounded-2xl p-5 mb-4" style={{ background: "#1a1a1a", border: "1px solid rgba(200,150,12,0.35)" }}>
                <div style={{ height: 2, background: "linear-gradient(90deg,transparent,#C8960C,transparent)", marginBottom: 16, borderRadius: 1 }} />
                <h3 style={{ color: "#FFD700", fontFamily: "serif", fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Mukhi Count</h3>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, background: "rgba(200,150,12,0.07)", border: "1px solid rgba(200,150,12,0.2)", marginBottom: 12 }}>
                  <span style={{ color: "#C8960C", fontSize: 13, fontWeight: 700 }}>Detected Mukhi</span>
                  <span style={{ color: "#FFD700", fontSize: 24, fontWeight: 900 }}>{mukhis.length}</span>
                </div>

                <p style={{ fontSize: 11, color: "#5a4a30", lineHeight: 1.7 }}>
                  Click <span style={{ color: "#FFD700", fontWeight: 700 }}>✦ Auto Detect</span> to let AI count and place mukhi markers automatically.<br /><br />
                  You can also click on the image to add points manually, or drag existing ones to adjust.
                </p>
              </div>

              {/* Size indicator */}
              <div className="rounded-2xl p-5 mb-4" style={{ background: "#1a1a1a", border: "1px solid rgba(200,150,12,0.25)" }}>
                <h3 style={{ color: "#FFD700", fontFamily: "serif", fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Size Indicator</h3>
                <div className="space-y-3">
                  <div>
                    <label style={{ display: "block", fontSize: 10, color: "#8A6000", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 5 }}>Size (mm)</label>
                    <input className="input-field w-full" value={sizeText}
                      onChange={(e) => setSizeText(e.target.value)} placeholder="e.g. 19-21" />
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={showSize} onChange={(e) => setShowSize(e.target.checked)}
                      style={{ accentColor: "#C8960C", width: 14, height: 14 }} />
                    <span style={{ fontSize: 12, color: "#8A6000" }}>Show size indicator</span>
                  </label>
                </div>
              </div>

              {/* Mukhi list */}
              {mukhis.length > 0 && (
                <div className="rounded-2xl p-4" style={{ background: "#1a1a1a", border: "1px solid rgba(200,150,12,0.15)" }}>
                  <p style={{ fontSize: 10, color: "#8A6000", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>Mukhi Points</p>
                  <div className="space-y-2">
                    {mukhis.map((m, idx) => (
                      <div key={m.id} onClick={() => setSelectedId(m.id)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer"
                        style={{ background: m.id === selectedId ? "rgba(200,150,12,0.1)" : "rgba(255,255,255,0.03)", border: `1px solid ${m.id === selectedId ? "rgba(200,150,12,0.4)" : "transparent"}` }}>
                        <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#5a2d0c", border: "1.5px solid #8B4513", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <span style={{ color: "#fff", fontSize: 10, fontWeight: 800 }}>{idx + 1}</span>
                        </div>
                        <span style={{ flex: 1, fontSize: 12, color: "#f5f0e8", fontWeight: 600 }}>Mukhi {idx + 1}</span>
                        <button onClick={(e) => { e.stopPropagation(); removeMukhi(m.id); }}
                          style={{ color: "#7f1d1d", background: "none", border: "none", cursor: "pointer", fontSize: 14, padding: "0 2px" }}>✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p style={{ fontSize: 10, color: "#3a2a00", textAlign: "center", marginTop: 14, lineHeight: 1.7 }}>
                AI-powered mukhi detection<br />Download saves at 3× quality
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
